import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isApiRequestError } from './api-error';
import { toastError } from './notify';

/**
 * TanStack Query defaults for a WMS.
 *
 * - `staleTime: 30s` + `refetchOnWindowFocus: false`: lists are kept fresh by
 *   SSE (`useLiveQuery` invalidates on the matching topic), so polling and
 *   focus-refetch would be pure noise on a floor tablet that regains focus
 *   every time it wakes.
 * - Retries never apply to 4xx: a 403 or a validation error will not become a
 *   200 by asking again, and retrying a rejected write is how you get double
 *   stock movements.
 * - Errors are toasted centrally (localized from the `{code, params}` envelope)
 *   so no call site has to remember to.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (isApiRequestError(error)) {
            if (error.isNetworkError) return failureCount < 2;
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 1;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // A background refetch that fails while cached data is on screen must
        // not shout — the user is still looking at usable rows.
        if (query.state.data !== undefined) return;
        toastError(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        // A mutation with its own onError owns the messaging.
        if (mutation.options.onError) return;
        toastError(error);
      },
    }),
  });
}
